<script setup lang="ts">
import { useDistributorStations, useTransferStation } from "@/api/station-query";
import { __SOFTDB_ID__ } from "@/constants.ts";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { useQueryManager } from "@/stores/query-manager";
import { mdiLinkOff } from "@mdi/js";
import { ObjectSummary, Station } from "@soft-db/monitoring-api-client";
import { Button, confirm, Icon, makeColumnHelper, Tooltip } from "@soft-db/ui";
import AddStationDialog from "../common/AddStationDialog.vue";
import AppDataTable from "../common/AppDataTable.vue";
import ColumnName from "../common/ColumnName.vue";
import ConfirmationDialog from "../dialogs/ConfirmationDialog.vue";
import SiteLinkedBadge from "../sites/SiteLinkedBadge.vue";
import NavBarStatus from "../workspace/NavBarStatus.vue";

type DistStationRowData = {
	id: string;
	name: string;
	sn: string;
	description: string;
	site: ObjectSummary | null;
};

const props = defineProps<{ id: string }>();
const id = computed(() => props.id);

const { t } = useI18n();

const snackbar = useSnackbarStore();

const { stations } = useDistributorStations(id);
const stationsId = computed(() => stations.value?.map((s) => s.id));

const transferStation = useTransferStation();

const stationRows = computed<DistStationRowData[]>(() => {
	if (!stations.value) return [];

	return stations.value.map((s) => ({
		id: s.id,
		name: s.name,
		sn: s.serialNumber,
		description: s.description,
		site: s.site,
	}));
});

const { Column } = makeColumnHelper<DistStationRowData>();

const addStationDialog = ref(false);
async function openAddStationDialog() {
	addStationDialog.value = true;
}

const saveConfirmationRef = useTemplateRef("$stationConfirmation");

const queryManager = useQueryManager();
async function transferStations(stations: Station[]) {
	if (saveConfirmationRef.value == null) return;

	const { isCanceled } = await saveConfirmationRef.value.reveal(stations);
	if (isCanceled) {
		return;
	}

	await queryManager.chainInvalidation(async () => {
		for (const station of stations) {
			await transferStation.mutateAsync({
				id: station.id,
				dist: id.value,
			});
		}
	});
	snackbar.openOnSaved();
}

async function removeStation(stationId: string, name: string) {
	if (await confirm.yesNo(t("distributor.removeStationConfirmation", { n: name }))) {
		await transferStation.mutateAsync({ id: stationId, dist: __SOFTDB_ID__ });
		snackbar.openOnSaved();
	}
}
</script>

<template>
	<ConfigSection
		:title="t('object.station', 2)"
		:description="t('distributor.stationsDescription')"
	>
		<AppDataTable
			layout="contained"
			:data="stationRows"
			:pagination="{ pageIndex: 0, pageSize: 10 }"
		>
			<template #action>
				<Button variant="outline" @click="openAddStationDialog">
					{{ t("space.addStation") }}
				</Button>
			</template>
			<template #columns>
				<Column
					v-slot="{ row: { original: station } }"
					field="name"
					:header="t('common.name')"
					enableSorting
					enableGlobalFilter
				>
					<ColumnName :name="station.name" :description="station.description" />
				</Column>

				<Column field="sn" :header="t('station.serialNumber')" enableSorting enableGlobalFilter />

				<Column
					id="site"
					v-slot="{ row: { original: station } }"
					:fieldFn="(d) => d.site?.name || ''"
					:header="t('object.project', 1)"
					enableSorting
					enableGlobalFilter
				>
					<SiteLinkedBadge :site="station.site" />
				</Column>

				<Column v-slot="{ row: { original: station } }" shrink>
					<div class="flex items-center gap-1">
						<NavBarStatus class="mr-4" sourceType="station" :sourceId="station.id" />
						<Tooltip :tooltip="t('space.unlinkStation')">
							<Button
								class="float-right"
								variant="soft"
								color="error"
								size="sm"
								@click="removeStation(station.id, station.sn)"
							>
								<Icon :d="mdiLinkOff" />
							</Button>
						</Tooltip>
					</div>
				</Column>
			</template>
		</AppDataTable>
	</ConfigSection>

	<AddStationDialog
		v-model:dialog="addStationDialog"
		:ignore="stationsId"
		@select="transferStations"
	/>

	<ConfirmationDialog
		ref="$stationConfirmation"
		:title="t('distributor.stationTransfer')"
		:description="t('distributor.stationTransferDescription')"
	/>
</template>
